// client/src/components/pages/LexiStar/PlanDispatch/useAllocatedDrivers.ts
//
// The plan grid's default driver (ST-00198): for every draft row with a truck, the driver
// allocated to that truck at the row's planned start (now when it has none) — GET
// /trips:resolveDriver, the same rule the server applies when the row is saved without a chosen
// driver. Asked again whenever a row's truck or planned start changes; one request per distinct
// (truck, instant), answers kept for the page's life.

import { useEffect, useMemo, useRef, useState } from 'react';
import { resolveTripDriver } from '../../../../services/lexistarTripDrivers';
import type { AllocationState, PlanRow } from './planTypes';

/** `${asset_id}|${planned start ISO, else 'now'}`; null without a truck. */
export const allocationKey = (row: Pick<PlanRow, 'asset_id' | 'planned_start'>): string | null =>
  row.asset_id
    ? `${row.asset_id}|${row.planned_start ? new Date(row.planned_start).toISOString() : 'now'}`
    : null;

export default function useAllocatedDrivers(
  rows: PlanRow[],
  { enabled = true }: { enabled?: boolean } = {}
): Map<string, AllocationState> {
  const [answers, setAnswers] = useState<Map<string, AllocationState>>(() => new Map());
  const asked = useRef(new Set<string>());
  const wanted = useMemo(
    () =>
      [
        ...new Set(
          rows
            .filter((r) => !r.saved)
            .map(allocationKey)
            .filter((k): k is string => !!k)
        ),
      ].join('\n'),
    [rows]
  );

  useEffect(() => {
    if (!enabled || !wanted) return;
    for (const key of wanted.split('\n')) {
      if (asked.current.has(key)) continue;
      asked.current.add(key);
      const [assetId, at] = key.split('|');
      setAnswers((prev) => new Map(prev).set(key, { status: 'loading' }));
      resolveTripDriver({ asset_id: assetId, at: at === 'now' ? undefined : at })
        .then((result) => setAnswers((prev) => new Map(prev).set(key, { status: 'done', result })))
        .catch(() => setAnswers((prev) => new Map(prev).set(key, { status: 'error' })));
    }
  }, [wanted, enabled]);

  return answers;
}
